import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ReportJobStatus, ReportJobStatusDto, ReportService, ReportType } from '../../../services/report.service';
import { DateInputComponent } from '../../shared/date-input/date-input.component';
import { DistrictZoneFilterComponent } from '../../shared/district-zone-filter/district-zone-filter.component';

const REPORT_TYPE = ReportType.CustomerCollections;

type DatePreset = 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear' | 'custom';

@Component({
  selector: 'app-customer-collections-report',
  standalone: true,
  imports: [CommonModule, FormsModule, DateInputComponent, DistrictZoneFilterComponent],
  templateUrl: './customer-collections-report.component.html',
  styleUrl: './customer-collections-report.component.css'
})
export class CustomerCollectionsReportComponent implements OnInit, OnDestroy {
  readonly Status = ReportJobStatus;

  presets: { id: DatePreset; label: string; icon: string }[] = [
    { id: 'last7days', label: 'Last 7 Days', icon: 'fa-calendar-day' },
    { id: 'last30days', label: 'Last 30 Days', icon: 'fa-calendar-alt' },
    { id: 'thisMonth', label: 'This Month', icon: 'fa-calendar' },
    { id: 'lastMonth', label: 'Last Month', icon: 'fa-history' },
    { id: 'thisQuarter', label: 'This Quarter', icon: 'fa-chart-pie' },
    { id: 'thisYear', label: 'This Year', icon: 'fa-calendar-check' },
    { id: 'custom', label: 'Custom Range', icon: 'fa-sliders-h' }
  ];

  selectedPreset: DatePreset = 'last7days';
  fromDate = '';
  toDate = '';
  selectedZoneIds: string[] = [];

  generating = false;
  loadingHistory = true;
  recentJobs: ReportJobStatusDto[] = [];
  errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(private reportService: ReportService) { }

  ngOnInit(): void {
    this.applyPreset(this.selectedPreset);
    this.loadRecentJobs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectPreset(preset: DatePreset): void {
    this.selectedPreset = preset;
    this.applyPreset(preset);
  }

  applyPreset(preset: DatePreset): void {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (preset) {
      case 'last7days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        this.fromDate = this.formatDateForInput(start);
        this.toDate = this.formatDateForInput(today);
        break;
      }
      case 'last30days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        this.fromDate = this.formatDateForInput(start);
        this.toDate = this.formatDateForInput(today);
        break;
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        this.fromDate = this.formatDateForInput(start);
        this.toDate = this.formatDateForInput(today);
        break;
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        this.fromDate = this.formatDateForInput(start);
        this.toDate = this.formatDateForInput(end);
        break;
      }
      case 'thisQuarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), quarter * 3, 1);
        this.fromDate = this.formatDateForInput(start);
        this.toDate = this.formatDateForInput(today);
        break;
      }
      case 'thisYear': {
        const start = new Date(now.getFullYear(), 0, 1);
        this.fromDate = this.formatDateForInput(start);
        this.toDate = this.formatDateForInput(today);
        break;
      }
      case 'custom':
        // Keep whatever dates are already selected.
        break;
    }
  }

  generate(): void {
    if (!this.fromDate || !this.toDate) {
      this.errorMessage = 'Please select both a from and to date.';
      return;
    }
    if (this.fromDate > this.toDate) {
      this.errorMessage = 'From date must be on or before the to date.';
      return;
    }

    const duplicate = this.findDuplicateJob();
    if (duplicate) {
      const generatedAt = new Date(duplicate.createdAt).toLocaleString();
      if (!confirm(`A report for this exact date range was already generated at ${generatedAt}. Regenerate anyway?`)) {
        return;
      }
    }

    this.errorMessage = '';
    this.generating = true;

    this.reportService
      .startCustomerCollectionsReport({
        fromDate: this.fromDate,
        toDate: this.toDate,
        geoZoneIds: this.selectedZoneIds
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: job => {
          this.recentJobs = [job, ...this.recentJobs];
          this.trackJob(job.id);
        },
        error: () => {
          this.generating = false;
          this.errorMessage = 'Failed to start the report. Please try again.';
        }
      });
  }

  private loadRecentJobs(): void {
    this.loadingHistory = true;
    this.reportService
      .getRecentJobs(REPORT_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: jobs => {
          this.recentJobs = jobs;
          this.loadingHistory = false;
          const activeJob = jobs.find(j => j.status === ReportJobStatus.Pending || j.status === ReportJobStatus.Running);
          if (activeJob) {
            this.generating = true;
            this.trackJob(activeJob.id);
          }
        },
        error: () => {
          // Non-fatal — the form still works without job history.
          this.loadingHistory = false;
        }
      });
  }

  private trackJob(jobId: string): void {
    this.reportService
      .pollJobStatus(jobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          this.updateRecentJob(status);
          if (
            status.status === ReportJobStatus.Completed ||
            status.status === ReportJobStatus.Failed ||
            status.status === ReportJobStatus.Expired
          ) {
            this.generating = false;
          }
        },
        error: () => {
          this.generating = false;
          this.errorMessage = 'Failed to check report status. Please try again.';
        }
      });
  }

  private updateRecentJob(status: ReportJobStatusDto): void {
    const index = this.recentJobs.findIndex(j => j.id === status.id);
    if (index >= 0) {
      this.recentJobs = [
        ...this.recentJobs.slice(0, index),
        status,
        ...this.recentJobs.slice(index + 1)
      ];
    } else {
      this.recentJobs = [status, ...this.recentJobs];
    }
  }

  /** A completed report already covering the exact from/to date range currently selected, if any. */
  private findDuplicateJob(): ReportJobStatusDto | undefined {
    return this.recentJobs.find(
      j =>
        j.status === ReportJobStatus.Completed &&
        j.fromDate.substring(0, 10) === this.fromDate &&
        j.toDate.substring(0, 10) === this.toDate
    );
  }

  private formatDateForInput(date: Date): string {
    // Build from local date parts, not toISOString() — that converts to UTC first, which
    // shifts the date back a day for any timezone ahead of UTC (e.g. IST, UTC+5:30).
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
